import { formatRiskCell } from 'tools/helper'
import { Chip, Tooltip } from 'components'
import strings from 'strings'
import { numberComparator, numberOrStringComparator } from 'tools/comparators'
import { GridRenderCellParams } from '@mui/x-data-grid'
import { teamVehicleStatus, vehicleStatus } from 'constants/status'
import { AssignedDriver } from 'interfaces/IDrivers'
import React from 'react'
import styles from './assignedDriversCell.module.scss'

const t = strings.TABLE_DEFAULTS

const AssignedDriversCell: React.FC<{
  drivers: AssignedDriver[]
  truncateDriverDisplay: (
    name: string,
    id: string,
    maxLength?: number
  ) => { name: string; id: string }
}> = ({ drivers, truncateDriverDisplay }) => {
  if (!drivers || drivers.length === 0) {
    return (
      <div className={styles.emptyState}>
        <span>{t.DASH_VALUE}</span>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {drivers.map((driver, index) => {
        const { name: displayName, id: displayId } = truncateDriverDisplay(
          driver.driverName,
          driver.eldDriverId
        )
        const fullDisplayText = `${driver.driverName} (${driver.eldDriverId})`

        return (
          <div
            key={`${driver.eldDriverId}-${index}`}
            className={styles.driverItem}
          >
            <Tooltip title={fullDisplayText} containerClassName="ellipsis">
              <span>
                <span className={styles.driverName}>{displayName}</span>
                <span className={styles.driverId}>{displayId}</span>
              </span>
            </Tooltip>
          </div>
        )
      })}
    </div>
  )
}

const getVehicleAssignmentsTableColumns = (
  truncateDriverDisplay: (
    name: string,
    id: string,
    maxLength?: number
  ) => { name: string; id: string }
) => {
  return [
    {
      field: 'rowId',
      headerName: 'ID',
      width: 60,
      minWidth: 60,
      flex: 0.3,
    },
    {
      field: 'model',
      headerName: 'Model',
      width: 140,
      minWidth: 140,
      flex: 0.8,
    },
    {
      field: 'plateNumber',
      headerName: 'Plate Number',
      width: 120,
      minWidth: 120,
      flex: 0.7,
      sortable: true,
      sortComparator: numberOrStringComparator,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip title={params.value} containerClassName="ellipsis">
            {params.value}
          </Tooltip>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      minWidth: 90,
      flex: 0.4,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Chip
            size="small"
            label={
              params.row.status
                ? vehicleStatus.ACTIVE.label
                : vehicleStatus.INACTIVE.label
            }
            variant="filled"
            sx={{
              backgroundColor: params.row.status
                ? vehicleStatus.ACTIVE.background
                : vehicleStatus.INACTIVE.background,
              color: params.row.status
                ? vehicleStatus.ACTIVE.color
                : vehicleStatus.INACTIVE.color,
            }}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return value ? vehicleStatus.ACTIVE.label : vehicleStatus.INACTIVE.label
      },
    },
    {
      field: 'miles',
      headerName: 'Miles (Last 30 Days)',
      width: 140,
      minWidth: 140,
      flex: 0.8,
      sortable: true,
      sortComparator: numberComparator,
      valueGetter: (value: any, row: any) => {
        return row.miles !== undefined && row.miles !== null
          ? formatRiskCell(row.miles)
          : t.DASH_VALUE
      },
    },
    {
      field: 'teamVehicle',
      headerName: 'Team Vehicle',
      width: 100,
      minWidth: 100,
      flex: 0.5,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Chip
            size="small"
            label={
              params.row.teamVehicle
                ? teamVehicleStatus.YES.label
                : teamVehicleStatus.NO.label
            }
            variant="filled"
            sx={{
              backgroundColor: params.row.teamVehicle
                ? teamVehicleStatus.YES.background
                : teamVehicleStatus.NO.background,
              color: params.row.teamVehicle
                ? teamVehicleStatus.YES.color
                : teamVehicleStatus.NO.color,
            }}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return value ? teamVehicleStatus.YES.label : teamVehicleStatus.NO.label
      },
    },
    {
      field: 'assignedDrivers',
      headerName: 'Assigned Drivers',
      width: 160,
      minWidth: 160,
      flex: 1,
      sortable: false,
      cellClassName: 'assigned-drivers-cell',
      renderCell: function (params: GridRenderCellParams) {
        return (
          <AssignedDriversCell
            drivers={params.row.assignedDrivers || []}
            truncateDriverDisplay={truncateDriverDisplay}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        const drivers = row.assignedDrivers || []
        if (!Array.isArray(drivers) || drivers.length === 0) {
          return t.DASH_VALUE
        }
        return drivers
          .map(
            (driver: AssignedDriver) =>
              `${driver.driverName}(${driver.eldDriverId})`
          )
          .join(', ')
      },
    },
    {
      field: 'vin',
      headerName: 'VIN',
      width: 140,
      minWidth: 140,
      flex: 0.8,
      sortable: true,
      sortComparator: numberOrStringComparator,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip title={params.value} containerClassName="ellipsis">
            {params.value}
          </Tooltip>
        )
      },
    },
  ]
}

export { getVehicleAssignmentsTableColumns }
